import { EventTag, ProductRootCause, User, QualityEvent, QualityEventCreate } from '../types';
import { UseFormSetValue } from 'react-hook-form';
import { RegistryItemStatus } from '../types/registry';
import i18n from '../i18n/config';

export const filterTags = (userId: number, usersData: User[], tagsData: EventTag[]): EventTag[] => {
  const userGroups = usersData?.find((user) => user.id === userId)?.groups;
  return tagsData?.filter((tag) => {
    if (!tag.group_ids?.length) {
      return tag;
    }
    const groupIntersection = userGroups?.filter((groupId) => tag.group_ids?.includes(groupId));
    if (groupIntersection?.length) {
      return tag;
    }
    return false;
  });
};

export const filterOwnersByPrivateTags = (usersData: User[], tagsData: EventTag[], eventTags: number[]): User[] => {
  if (!eventTags) {
    return usersData;
  }

  const tagsFromEvents = tagsData.filter((tag) => eventTags.includes(tag.id));

  const groupIdsInEventTags: number[] = [];
  tagsFromEvents.forEach((tag) => {
    if (tag.group_ids?.length) {
      tag.group_ids.forEach((groupId) => {
        groupIdsInEventTags.push(groupId);
      });
    }
  });

  if (!groupIdsInEventTags.length) {
    return usersData;
  }

  return usersData.filter((user) => {
    if (!user.groups) {
      return false;
    }
    return user.groups.some((gId) => groupIdsInEventTags.includes(gId));
  });
};

const stringify = (val: unknown): string | undefined => (val === undefined || val === null ? undefined : String(val));

/**
 * Every registry item already linked to the event.
 * An archived item stays selectable only while it is in this list, so it has to cover them all.
 */
export const linkedRegistryItemIds = (linkedItems: ProductRootCause[] | undefined): string[] =>
  linkedItems?.map((item) => item.matrix_id) ?? [];

export const eventFieldsSetter = (event: QualityEvent | undefined, setValue: UseFormSetValue<QualityEventCreate>) => {
  if (!event) {
    return;
  }
  // Setting the below values so that we can use the edit event endpoint
  setValue('title', event.title);
  setValue('workflow_id', String(event.workflow_id));
  setValue('owner_id', String(event.owner_id));
  setValue('description', event.description);
  setValue('related_entity_ids', event.related_entity_ids);
  setValue('suppliers', event.suppliers);
  setValue('related_documents', event.related_documents);
  setValue('related_events', event.related_events);
  setValue('severity_id', stringify(event.severity_id));
  setValue('probability_id', stringify(event.probability_id));
  setValue('risk_id', stringify(event.risk_id));
  if (event.tags_ids) {
    setValue('tags_ids', event.tags_ids.map(String));
  }
  if (event.products) {
    setValue('related_products', event.products?.map((product: ProductRootCause) => product.matrix_id));
  }
  if (event.rootcauses) {
    setValue('related_rootcauses', event.rootcauses?.map((rootcause: ProductRootCause) => rootcause.matrix_id));
  }
};

export const isStepNotCompleted = (stepStatus: string): boolean => !['completed', 'closed'].includes(stepStatus);

export const registryItemLabel = (item: { name: string; status?: string }): string =>
  item.status === RegistryItemStatus.Archived
    ? i18n.t('common.labels.archivedRegistryItem', { name: item.name })
    : item.name;
